import { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export function PushNotificationToggle() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_KEY) return;
    setSupported(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  }, []);

  useEffect(() => { checkSubscription(); }, [checkSubscription]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      setSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (e) {
      if (Notification.permission === 'denied') {
        toast.error('Notifications are blocked. Please enable them in your browser settings.');
      } else {
        toast.error('Failed to enable notifications');
      }
    } finally { setLoading(false); }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.delete('/push/subscribe', { data: { endpoint: sub.endpoint } });
          await sub.unsubscribe();
        }
      }
      setSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); } finally { setLoading(false); }
  };

  if (!supported) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={subscribed ? unsubscribe : subscribe}
      disabled={loading}
      className={`rounded-full gap-2 text-xs font-bold ${subscribed ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
      data-testid="push-toggle-btn"
    >
      {subscribed ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
      {loading ? '...' : subscribed ? 'Notifications On' : 'Enable Notifications'}
    </Button>
  );
}
